import { useState } from "react";

import { ReadFailure } from "src/components/read-failure";
import { Empty, humanPopulation, Table } from "src/components/ui";
import {
  describeEvent,
  fieldChanges,
  type AuditEvent,
  type AuditNames,
  type AuditRow
} from "src/lib/audit";

/**
 * How the console renders audit history, wherever it renders it.
 *
 * Extracted from the identity screen when console access history got a screen of its
 * own (INFRA-685). Two panels reading the same table through the same describers had
 * to render a row the same way, and the alternative — a second copy of the row, its
 * expander and its before/after table — is a renderer that drifts silently, since
 * neither copy fails when the other changes. Everything about *what* is being listed
 * stays with the caller: its own Card, its own query, its own caption, and its own
 * account of whether the list is complete.
 *
 * The shape is fixed at four columns because the content is: when, who did it, what
 * happened, and the disclosure. Callers label the first three, which is the whole of
 * what differs — "Who" reads as the actor beside a person's own history, and as
 * "Changed by" beside a list of grants where the subject is in the row too.
 */
export function AuditEventList({
  events,
  names,
  isPending,
  error,
  head,
  empty
}: {
  events: readonly AuditEvent[];
  names: AuditNames | null;
  isPending: boolean;
  error: Error | null;
  /** The first three column labels; the fourth holds the expander and has none. */
  head: readonly [string, string, string];
  /** What to say when the read succeeded and there is nothing to show. */
  empty: string;
}) {
  return (
    <>
      {isPending ? <Empty>Loading…</Empty> : null}
      {error ? <ReadFailure error={error} /> : null}
      {!isPending && !error && events.length === 0 ? <Empty>{empty}</Empty> : null}
      {events.length > 0 ? (
        <Table head={[...head, ""]}>
          {events.map((event) => (
            <AuditRowView key={event.key} event={event} names={names} />
          ))}
        </Table>
      ) : null}
    </>
  );
}

function AuditRowView({ event, names }: { event: AuditEvent; names: AuditNames | null }) {
  const [open, setOpen] = useState(false);
  const { title, detail } = describeEvent(event);

  return (
    <>
      <tr className="border-b border-slate-100 align-top">
        <td className="whitespace-nowrap px-2 py-2 text-slate-600">
          {new Date(event.occurredAt).toLocaleString()}
        </td>
        <td className="px-2 py-2 text-slate-700">{event.actorEmail}</td>
        <td className="px-2 py-2">
          <span className="font-medium text-slate-900">{title}</span>
          {detail ? <span className="text-slate-600"> — {detail}</span> : null}
        </td>
        <td className="px-2 py-2 text-right">
          {/* aria-expanded, like the Edit button on an engagement row and the
              headcount on an organization row — the console's other two disclosures.
              The label already flips between Details and Hide, but that says what the
              button will do, not what state the row is in. */}
          <button
            type="button"
            aria-expanded={open}
            className="text-xs font-medium text-slate-600 underline hover:text-slate-900"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? "Hide" : "Details"}
          </button>
        </td>
      </tr>
      {open ? (
        <tr className="border-b border-slate-100 bg-slate-50">
          <td colSpan={4} className="px-2 py-3">
            <div className="flex flex-col gap-3">
              {event.rows.map((row) => (
                <AuditRowDetail key={row.id} row={row} names={names} />
              ))}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AuditRowDetail({ row, names }: { row: AuditRow; names: AuditNames | null }) {
  const changes = fieldChanges(row, names);

  return (
    <div>
      <div className="font-mono text-xs text-slate-500">
        {row.action}
        {row.population ? ` · ${humanPopulation(row.population)}` : null}
      </div>
      <div className="mt-1 text-xs text-slate-600">
        Actor IP: <span className="font-mono">{row.actorIp ?? "Not recorded"}</span>
      </div>
      {changes.length === 0 ? (
        <p className="mt-1 text-xs text-slate-500">No field values recorded.</p>
      ) : (
        <table className="mt-1 w-full text-xs">
          <tbody>
            {changes.map((change) => (
              <tr key={change.field}>
                <td className="w-52 py-0.5 pr-3 align-top text-slate-600">{change.field}</td>
                <td className="py-0.5 align-top text-slate-500">
                  {/* Driven by the action, not by the before value being empty: an
                      update that fills a blank field has both, and "— → Female" is
                      the answer, where showing only "Female" hides half of it. */}
                  {change.hadPriorState ? (
                    <>
                      <span className={change.before === "—" ? "" : "line-through"}>
                        {change.before}
                      </span>
                      <span className="px-1.5">→</span>
                    </>
                  ) : null}
                  <span className="text-slate-800">{change.after}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
